import {
  HttpClient,
  HttpParams,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { PagedResult } from '../models/common.models';
import {
  EmployeeSecondaryOfficeDto,
  OfficeResourceDto,
  ResourceDto,
  ResourceListRequest,
} from '../models/resource.models';

/**
 * Wraps the OfficesController. Manages the association of
 * employees to additional (secondary) offices.
 */
@Injectable({
  providedIn: 'root',
})
export class OfficeService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/offices`;

  /**
   * Get all active secondary office associations for an employee
   * (GET /api/offices/employee/{employeeId}).
   * Used by the user-offices dialog to list a person's secondary offices.
   */
  getByEmployeeId(
    employeeId: string,
  ): Observable<EmployeeSecondaryOfficeDto[]> {
    return this.http
      .get<EmployeeSecondaryOfficeDto[]>(
        `${this.apiUrl}/employee/${encodeURIComponent(employeeId)}`,
      );
  }

  /**
   * Get the resources for an office, enriched as paged OfficeResourceDtos (each row carries its
   * office membership). Server-side search + pagination; used by the office admin assigned-resources
   * list.
   */
  getAssignedResources(
    officeCode: string,
    page = 1,
    pageSize = 25,
    search?: string,
  ): Observable<PagedResult<OfficeResourceDto>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    if (search?.trim()) {
      params = params.set('search', search.trim());
    }
    return this.http
      .get<PagedResult<OfficeResourceDto>>(
        `${this.apiUrl}/office/${encodeURIComponent(officeCode)}/resources`,
        { params },
      );
  }

  /**
   * Search active employees for admin secondary-office management. Bypasses the
   * current user's practice-area access filter and supports excludeSecondaryOfficeCode.
   */
  searchResources(
    request: ResourceListRequest,
  ): Observable<PagedResult<ResourceDto>> {
    const body: Record<string, unknown> = {
      page: request.page,
      pageSize: request.pageSize,
    };
    if (request.search) {
      body['search'] = request.search;
    }
    if (request.excludeOfficeCode) {
      body['excludeOfficeCode'] = request.excludeOfficeCode;
    }
    if (request.sortBy) {
      body['sortBy'] = request.sortBy;
    }
    if (request.sortDirection) {
      body['sortDirection'] = request.sortDirection;
    }
    return this.http
      .post<PagedResult<ResourceDto>>(`${this.apiUrl}/resources/search`, body);
  }

  /**
   * Create a new secondary office association.
   */
  create(
    employeeId: string,
    officeCode: string,
  ): Observable<EmployeeSecondaryOfficeDto> {
    return this.http
      .post<EmployeeSecondaryOfficeDto>(this.apiUrl, { employeeId, officeCode });
  }

  /**
   * Soft delete a secondary office association by its id.
   */
  delete(employeeSecondaryOfficeId: number): Observable<void> {
    return this.http
      .delete<void>(`${this.apiUrl}/${employeeSecondaryOfficeId}`);
  }
}
